/* eslint-disable react/prop-types */
import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { matchPath, withRouter, Route } from 'react-router-dom';
import { Container } from 'reactstrap';
import { connect } from 'react-redux';
import { RoleHelper } from '@shoflo/polymer/helpers/RoleHelper/RoleHelper';
import PageHeader from 'containers/PageHeader';
import EventDashFolders from 'containers/EventDashFolders';
import { getEvent, startDuplicatingEvent } from 'actions/Event';
import { getTeam } from 'actions/Team';
import { showModal } from 'actions';
import { Nav, NavLink, NavItem } from 'reactstrap';
import EventDashCrew from '../EventDashCrew';
import EventDashTrash from '../EventDashTrash';
import UpcomingEventDropdown from 'containers/UpcomingEventDropdown';
import ReadOnlyBanner from 'components/event/ReadOnlyBanner';
import { getDateFormat } from 'utils/teamHelper';
import { getFormattedDateFromMillis } from 'utils/time';

const StyledNavLink = styled(NavLink)`
  border-top: none !important;
  border-left: none !important;
  border-right: none !important;
  font-size: 16px;
  cursor: pointer;
  font-weight: ${(props) => (props.active ? 'bold' : 'normal')};
  color: ${(props) =>
    props.active ? '#0095FF !important;' : '#767676 !important;'};
  border-bottom: ${(props) =>
    props.active ? '2px solid #0095FF !important;' : ''};
  margin: 0 16px;
  padding: 8px 0;

  &:hover {
    border: none;
    color: ${(props) =>
      props.active ? '#0095FF !important;' : '#333 !important;'};
  }
`;

const NavWrapper = styled.div`
  position: sticky;
  top: 212px;
  background: #fff;
  width: 100vw;
  margin: 0 -48px;
  margin-bottom: -4px;
  border-bottom: 2px solid #c8c8c8;
  z-index: 3;
  display: flex;
  padding-top: 8px;

  @media only screen and (max-width: 576px) {
    top: 192px;
  }
`;

const StyledNavTabs = styled(Nav)`
  border-bottom: none !important;
  margin: 0 32px;

  .nav-item {
    margin-bottom: -2px;
  }
`;
const ReadOnlyBannerWrapper = styled.div`
  position: relative;
  top: -8px;
  flex: 1;
`;
const EventDash = (props) => {
  const {
    user,
    backToUserDash,
    event,
    getEvent,
    match,
    team,
    getTeam,
    history,
    selectedEventFSFolder,
  } = props;

  const { params } = match;
  const { teamId, eventId } = params;

  useEffect(() => {
    if (user) getEvent(teamId, eventId);
  }, [teamId, eventId, getEvent, user]);

  useEffect(() => {
    getTeam(teamId);
  }, [getTeam, teamId]);

  useEffect(() => {
    // In support of https://github.com/SHOFLO/Issues/issues/5086
    // Basically what we are doing is preventing the user from viewing the "globalTemplateContainer" special event as if
    // it were a "normal" event
    if (event && event.globalTemplateContainer) {
      history.replace(`/dash/team/${teamId}`);
    }
  }, [event, history, teamId]);

  const teams = user ? user.teams : [];

  //waiting for the user to load as well prevents the sticky positions from being rendered incorrectly
  if (!event || !user) return <Container />;

  const eventTitle = event ? event.title : '';
  const eventImage = event ? event.photo : '';
  const dateFormat = getDateFormat(team || {});
  const startOn = getFormattedDateFromMillis(
    event.startOn,
    dateFormat,
    event.timeZone,
  );
  const endOn = getFormattedDateFromMillis(
    event.endOn,
    dateFormat,
    event.timeZone,
  );

  if (document.title !== event.title) document.title = event.title;

  const teamPhoto = team ? team.photo : '';
  const teamTitle = team ? team.title : '';

  const userTeamIds = teams.map((team) => team.team);
  const userHasTeam = userTeamIds.includes(teamId);

  const getBackLink = () => {
    if (
      selectedEventFSFolder &&
      history.location.pathname.includes('/folder/')
    ) {
      const { parentId } = selectedEventFSFolder;
      const parentFolderPath = `/dash/team/${teamId}/event/${eventId}/folder/${parentId}`;
      const dashboardPath = `/dash/team/${teamId}/event/${eventId}/dashboard`;
      return parentId ? parentFolderPath : dashboardPath;
    }

    return userHasTeam && !backToUserDash
      ? `/dash/team/${teamId}${event.archived ? '/archived' : ''}`
      : `/dash/user`;
  };

  return (
    <Container fluid>
      <PageHeader
        title={<UpcomingEventDropdown />}
        subtitle={`${startOn} — ${endOn}`}
        backTo={getBackLink()}
        centerImage={eventImage}
        centerImageTitle={`${eventTitle} event photo`}
        rightImage={teamPhoto}
        rightImageTitle={teamTitle}
        onRightImageClick={() => {
          if (!team) return;
          window.location.assign(`/dash/team/${team._id}`);
        }}
      />
      <Route path={match.url} component={ConnectedTabs} />
      <Route
        exact
        path={`${match.url}/dashboard`}
        component={EventDashFolders}
      />
      <Route
        exact
        path={`${match.url}/folder/:folderId`}
        component={EventDashFolders}
      />
      <Route exact path={`${match.url}/crew`} component={EventDashCrew} />
      <Route exact path={`${match.url}/trash`} component={EventDashTrash} />
    </Container>
  );
};

function Tabs({
  eventShowTemplates,
  startDuplicatingEvent,
  team,
  event,
  history,
  user,
}) {
  const navRef = useRef();

  const [navTop, setNavTop] = useState(0);

  useEffect(() => {
    setNavTop(navRef.current.getBoundingClientRect().top);
  }, [event]);

  const teamId = event.team._id ? event.team._id : event.team;

  const isEventAdmin = RoleHelper.hasEventAdminRole(user, teamId, event._id);

  const matchedPath = matchPath(window.location.pathname, {
    path: '/dash/team/:teamId/event/:eventId/:tab',
    strict: false,
  });

  const { params } = matchedPath;

  const tab = params.tab;

  return (
    <NavWrapper ref={navRef} stickyTop={`${navTop}px`}>
      <StyledNavTabs tabs>
        <NavItem>
          <StyledNavLink
            data-testid="dashboardTab"
            active={tab === 'dashboard' || tab === 'folder'}
            onClick={() => {
              history.push(`/dash/team/${teamId}/event/${event._id}/dashboard`);
            }}
          >
            Dashboard
          </StyledNavLink>
        </NavItem>
        <NavItem>
          <StyledNavLink
            data-testid="crewTab"
            active={tab === 'crew'}
            onClick={() => {
              history.push(`/dash/team/${teamId}/event/${event._id}/crew`);
            }}
          >
            Crew
          </StyledNavLink>
        </NavItem>
        {isEventAdmin && (
          <NavItem className="d-none d-sm-none d-md-block">
            <StyledNavLink
              data-testid="archivedTab"
              active={tab === 'trash'}
              onClick={() => {
                history.push(`/dash/team/${teamId}/event/${event._id}/trash`);
              }}
            >
              Archived
            </StyledNavLink>
          </NavItem>
        )}
        {!isEventAdmin ||
        !eventShowTemplates ||
        eventShowTemplates.length < 1 ? null : (
          <NavItem className="d-none d-sm-none d-md-block">
            <StyledNavLink
              data-testid="templatesTab"
              active={tab === 'templates'}
              onClick={() => {
                history.push(
                  `/dash/team/${teamId}/event/${event._id}/templates`,
                );
              }}
            >
              Show templates
            </StyledNavLink>
          </NavItem>
        )}
      </StyledNavTabs>
      <ReadOnlyBannerWrapper>
        <ReadOnlyBanner
          team={team}
          event={event}
          onDuplicateClick={startDuplicatingEvent}
        />
      </ReadOnlyBannerWrapper>
    </NavWrapper>
  );
}

const ConnectedTabs = withRouter(
  connect(
    ({ team, event, user, eventShowTemplates }) => {
      return { team, event, user, eventShowTemplates };
    },
    {
      startDuplicatingEvent,
    },
  )(Tabs),
);

export default withRouter(
  connect(
    ({ event, team, user, backToUserDash, selectedEventFSFolder }) => {
      return {
        event,
        team,
        user,
        backToUserDash,
        selectedEventFSFolder,
      };
    },
    {
      getEvent,
      getTeam,
      showModal,
    },
  )(EventDash),
);

export { Tabs };
